/**
 * JAISIU-4449 — Control UI client for the prepared session-file preview.
 *
 * The gateway's session-file prepare step already turns an inbound
 * attachment into *prepared* derivatives:
 *
 *   tabular  → `prep/schema.json` (columns + row count + sample rows)
 *   document → `prep/extracted.txt` (the extracted text)
 *
 * JAISIU-4448 exposed those derivatives over HTTP:
 *
 *   GET /api/session/files/<fileId>/content?sessionKey=…
 *
 * This module is the browser-side reader for that endpoint. It turns the
 * wire payload into the narrow shape the attachment model carries
 * ({@link import("./attachment-types.ts").TabularPreview} for spreadsheets,
 * plain text for documents) and it keeps the gateway's *honest* answer
 * intact: when the file has no derivable preview, the gateway answers
 * `200 { ok: true, available: false, reason }` and this module reports
 * "unavailable" rather than inventing empty columns or an empty string.
 *
 * Nothing here downloads or parses the original bytes — the browser only
 * ever sees the prepared preview, exactly as the agent's
 * `read_session_file` tool does.
 */

import { buildSessionFileUrl } from "./session-files.ts";
import type { DocumentBlock, TabularPreview, TabularSheetPreview } from "./attachment-types.ts";

/**
 * Why a preview is unavailable.
 *
 * Mirrors the gateway's `SessionFilePreviewUnavailableReason`:
 *   - `not-prepared`     — prep never ran for this file.
 *   - `no-derivative`    — prep ran but produced no readable derivative.
 *   - `unsupported-kind` — the file kind has no text/tabular preview.
 *   - `extraction-empty` — extraction produced no characters.
 */
export type PreviewUnavailableReason =
  | "not-prepared"
  | "no-derivative"
  | "unsupported-kind"
  | "extraction-empty"
  | "not-found"
  | "unauthorized"
  | "error";

/** The prepared preview of one session file. */
export type SessionFilePreviewResult =
  | {
      available: true;
      /** Present for tabular previews. */
      tabular?: TabularPreview;
      /** Present for document / text previews. */
      text?: string;
      /**
       * JAISIU-4479 — present for document previews that carry a
       * structured derivative: the headings, lists, paragraphs and
       * tables the gateway extracted from the file.
       *
       * Optional — absent for a PDF, a plain-text file, a DOCX whose
       * OOXML could not be parsed, and for any file whose prep never
       * ran. The caller then renders the flat extracted text (or the
       * honest "not analysed" message when there is no text either).
       */
      blocks?: DocumentBlock[];
    }
  | {
      available: false;
      reason: PreviewUnavailableReason;
      detail?: string;
    };

/** The raw wire shape of `GET /api/session/files/<id>/content`. */
interface PreviewWireResponse {
  ok?: boolean;
  fileId?: string;
  filename?: string;
  mediaType?: string;
  kind?: string;
  available?: boolean;
  reason?: string;
  detail?: string;
  preview?: {
    type?: string;
    columns?: Array<{ name?: string; type?: string } | string>;
    rowCount?: number;
    sampleRows?: string[][];
    /** JAISIU-3533 — every data row of the primary sheet. */
    rows?: string[][];
    /** JAISIU-3533 — rows above the detected header. */
    preamble?: string[][];
    /** JAISIU-3533 — every sheet of a multi-sheet workbook. */
    sheets?: Array<{
      name?: string;
      index?: number;
      columns?: Array<{ name?: string; type?: string } | string>;
      rows?: string[][];
      rowCount?: number;
      preamble?: string[][];
    }>;
    text?: string;
    textChars?: number;
    pageCount?: number;
    /**
     * JAISIU-4479 — the structured document derivative. Additive: the
     * gateway keeps `type: "text"` and the flat `text` untouched, and
     * only grows this optional field for a document whose structure
     * could be extracted.
     */
    blocks?: unknown;
  };
}

/** Normalize a gateway column entry (object or bare string) to a name. */
function columnName(column: { name?: string; type?: string } | string | undefined): string {
  if (typeof column === "string") { return column; }
  if (column && typeof column.name === "string") { return column.name; }
  return "";
}

/**
 * Normalize a row to an array of cell strings.
 *
 * The gateway sends `string[][]`, but a hand-rolled / older payload may
 * carry non-string cells; `String(cell)` keeps the table render total
 * instead of throwing on `undefined`.
 */
function normalizeRow(row: unknown): string[] {
  if (!Array.isArray(row)) { return []; }
  return row.map((cell) => (cell === null || cell === undefined ? "" : String(cell)));
}

/**
 * JAISIU-4479 — normalize one structured document block.
 *
 * The blocks come from a derivative written to disk, so they can be
 * truncated or malformed. Every field is validated here; a block that
 * does not validate is dropped rather than shipped to the renderer.
 *
 * Returns `null` for an invalid block.
 */
function normalizeDocumentBlock(value: unknown): DocumentBlock | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const block = value as Record<string, unknown>;
  switch (block.type) {
    case "heading": {
      const level = block.level;
      if (
        typeof level !== "number" ||
        !Number.isInteger(level) ||
        level < 1 ||
        level > 6 ||
        typeof block.text !== "string"
      ) {
        return null;
      }
      return { type: "heading", level: level as 1 | 2 | 3 | 4 | 5 | 6, text: block.text };
    }
    case "paragraph": {
      if (typeof block.text !== "string") {
        return null;
      }
      return { type: "paragraph", text: block.text };
    }
    case "list": {
      if (block.ordered !== true && block.ordered !== false) {
        return null;
      }
      if (!Array.isArray(block.items)) {
        return null;
      }
      const items = block.items
        .map((item) => {
          if (!item || typeof item !== "object") {
            return null;
          }
          const entry = item as Record<string, unknown>;
          if (typeof entry.text !== "string") {
            return null;
          }
          const rawLevel = entry.level;
          const level =
            typeof rawLevel === "number" && Number.isInteger(rawLevel) && rawLevel >= 1
              ? rawLevel
              : 1;
          return {
            marker: typeof entry.marker === "string" ? entry.marker : "",
            level,
            text: entry.text,
          };
        })
        .filter((item): item is { marker: string; level: number; text: string } => item !== null);
      if (items.length === 0) {
        return null;
      }
      return { type: "list", ordered: block.ordered, items };
    }
    case "table": {
      if (!Array.isArray(block.rows)) {
        return null;
      }
      const rows = block.rows
        .filter((row): row is unknown[] => Array.isArray(row))
        .map((row) => row.map((cell) => (cell === null || cell === undefined ? "" : String(cell))));
      if (rows.length === 0) {
        return null;
      }
      return { type: "table", rows };
    }
    default:
      return null;
  }
}

/**
 * JAISIU-4479 — normalize the `preview.blocks` payload.
 *
 * Returns `undefined` when the field is absent or carries no valid
 * block — the renderer then falls back to the flat text / honest
 * "not analysed" message.
 */
export function normalizeDocumentBlocks(value: unknown): DocumentBlock[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const blocks = value
    .map((block) => normalizeDocumentBlock(block))
    .filter((block): block is DocumentBlock => block !== null);
  return blocks.length > 0 ? blocks : undefined;
}

/**
 * Turn the wire payload into the narrow result shape.
 *
 * Exported for tests: the mapping is pure, so the "tabular payload
 * becomes columns + sample rows" and "empty text is unavailable, not
 * an empty preview" contracts can be asserted without a network.
 */
export function parseSessionFilePreview(payload: unknown): SessionFilePreviewResult {
  if (!payload || typeof payload !== "object") {
    return { available: false, reason: "error", detail: "malformed preview response" };
  }
  const body = payload as PreviewWireResponse;
  if (body.ok !== true) {
    return { available: false, reason: "error", detail: "gateway did not return a preview" };
  }
  if (body.available !== true || !body.preview) {
    const reason = (body.reason ?? "not-prepared") as PreviewUnavailableReason;
    return {
      available: false,
      reason,
      ...(body.detail ? { detail: body.detail } : {}),
    };
  }

  const preview = body.preview;
  if (preview.type === "tabular") {
    const columns = (preview.columns ?? []).map(columnName).filter((name) => name.length > 0);
    const sampleRows = (preview.sampleRows ?? []).map(normalizeRow);
    // JAISIU-3533 — the gateway ships the *whole* table, not a five-row
    // excerpt. Older gateways only send `sampleRows`; fall back to it.
    const rows = Array.isArray(preview.rows) ? preview.rows.map(normalizeRow) : undefined;
    const preamble = Array.isArray(preview.preamble)
      ? preview.preamble.map(normalizeRow)
      : undefined;
    const sheets = Array.isArray(preview.sheets)
      ? preview.sheets
          .map((sheet, index): TabularSheetPreview => ({
            name: typeof sheet.name === "string" ? sheet.name : "",
            index: typeof sheet.index === "number" ? sheet.index : index,
            columns: (sheet.columns ?? []).map(columnName).filter((name) => name.length > 0),
            rows: (sheet.rows ?? []).map(normalizeRow),
            rowCount: typeof sheet.rowCount === "number" ? sheet.rowCount : 0,
            preamble: (sheet.preamble ?? []).map(normalizeRow),
          }))
          .filter((sheet) => sheet.columns.length > 0 || sheet.rows.length > 0)
      : undefined;

    // A tabular preview with neither columns nor rows carries nothing to
    // show; treat it as unavailable rather than rendering an empty table.
    const visibleRows = rows ?? sampleRows;
    if (columns.length === 0 && visibleRows.length === 0) {
      return { available: false, reason: "extraction-empty" };
    }
    return {
      available: true,
      tabular: {
        columns,
        rowCount:
          typeof preview.rowCount === "number" ? preview.rowCount : visibleRows.length,
        sampleRows,
        rows,
        preamble,
        sheets,
      },
    };
  }

  if (preview.type === "text") {
    const text = (preview.text ?? "").trim();
    // JAISIU-4479 — the structured derivative is *additive*: it never
    // replaces the flat text (the model-facing contract) and it never
    // makes an empty extraction look available. A document with
    // blocks but no text still has nothing to show as a body, so it
    // stays unavailable — the blocks alone are not a preview of a
    // *document body*, they are a view of one.
    const blocks = normalizeDocumentBlocks(preview.blocks);
    if (!text) {
      return { available: false, reason: "extraction-empty" };
    }
    return {
      available: true,
      text,
      ...(blocks ? { blocks } : {}),
    };
  }

  return { available: false, reason: "unsupported-kind" };
}

/**
 * Fetch the prepared preview for one stored session file.
 *
 * Returns an honest "unavailable" result for every failure mode —
 * a missing file, a rejected token, a network error — so the caller
 * never has to distinguish "no preview" from "the fetch broke" when
 * rendering: both end in the same honest message.
 */
export async function fetchSessionFilePreview(args: {
  origin: string;
  token: string | undefined;
  sessionKey: string;
  fileId: string;
  basePath?: string;
  /** Injectable for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}): Promise<SessionFilePreviewResult> {
  if (!args.fileId || !args.sessionKey) {
    return { available: false, reason: "not-found" };
  }
  const url = buildSessionFileUrl({
    origin: args.origin,
    basePath: args.basePath,
    sessionKey: args.sessionKey,
    fileId: args.fileId,
    // JAISIU-4448 — the `content` variant serves the prepared preview
    // payload as JSON (tabular columns + sample rows, or extracted
    // document text) rather than the original bytes.
    variant: "content",
  });
  const doFetch = args.fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== "function") {
    return { available: false, reason: "error", detail: "fetch is unavailable" };
  }
  let resp: Response;
  try {
    resp = await doFetch(url, {
      headers: args.token ? { Authorization: `Bearer ${args.token}` } : {},
      credentials: "include",
    });
  } catch (err) {
    return {
      available: false,
      reason: "error",
      detail: err instanceof Error ? err.message : "could not reach the gateway",
    };
  }
  if (resp.status === 401 || resp.status === 403) {
    return { available: false, reason: "unauthorized" };
  }
  if (resp.status === 404) {
    return { available: false, reason: "not-found" };
  }
  if (!resp.ok) {
    return { available: false, reason: "error", detail: `preview request failed (${resp.status})` };
  }
  let payload: unknown;
  try {
    payload = await resp.json();
  } catch {
    return { available: false, reason: "error", detail: "preview response was not JSON" };
  }
  return parseSessionFilePreview(payload);
}
